import { Price, Token } from '@kodiak-finance/sdk-core'
import { FeeAmount } from '@kodiak-finance/v3-sdk'

import { Bound } from '../state/mint/v3/actions'
import { formatPrice } from './formatCurrencyAmount'

/**
 * Formats a price based on tick limits and thresholds
 * @param price The price to format
 * @param atLimit Whether the tick is at its lower or upper limit
 * @param direction Whether this is the lower or upper bound of the range
 * @param placeholder A placeholder string if price is undefined
 * @returns A formatted price string
 */

/**
 * Determines optimal number of significant figures based on price value
 * @param price The price to analyze
 * @returns Number of significant figures to use
 */
export function getOptimalSigFigs(price: Price<Token, Token> | undefined | null): number {
  if (!price) return 3

  try {
    const priceNumber = Number(price.toSignificant(10))

    if (priceNumber >= 100000) return 6
    if (priceNumber >= 10000) return 5
    if (priceNumber >= 1000) return 4

    return 3
  } catch (error) {
    return 3
  }
}

export function formatTickPrice(
  price: Price<Token, Token> | undefined,
  atLimit: { [bound in Bound]?: boolean | undefined },
  direction: Bound,
  feeAmount?: number,
  placeholder?: string
): string {
  if (!price) {
    return placeholder !== undefined ? placeholder : ''
  }

  const optimalSigFigs = getOptimalSigFigs(price)

  try {
    const priceValue = parseFloat(price.toFixed(10))

    if (atLimit[direction]) {
      if (direction === Bound.LOWER && priceValue < 1e-6) return '0'
      if (direction === Bound.UPPER && priceValue > 1e6) return '∞'
    }

    if (priceValue < 1e-15) return '0'
    if (priceValue > 1e10) return direction === Bound.UPPER ? '∞' : priceValue.toExponential(2)

    const is1BpsTier = feeAmount === FeeAmount.LOWEST

    if (is1BpsTier) {
      return formatPrice(price, 6)
    } else {
      return formatPrice(price, optimalSigFigs)
    }
  } catch (error) {
    return formatPrice(price, optimalSigFigs)
  }
}

/**
 * Formats a price based on tick limits and thresholds
 * @param price The price to format
 * @param atLimit Whether the tick is at its lower or upper limit
 * @param direction Whether this is the lower or upper bound of the range
 * @param placeholder A placeholder string if price is undefined
 * @returns A formatted price string
 */
export function formatTickPriceForRangeDisplay(
  price: Price<Token, Token> | undefined | null,
  isPriceActuallyAtLimit: boolean | undefined,
  displayBound: Bound,
  feeAmount?: number,
  placeholder?: string
): string {
  if (isPriceActuallyAtLimit) {
    return displayBound === Bound.LOWER ? '0' : '∞'
  }

  if (!price) {
    return placeholder !== undefined ? placeholder : ''
  }

  const optimalSigFigs = getOptimalSigFigs(price)

  try {
    const priceValue = parseFloat(price.toSignificant(10))

    // Add threshold for "effectively zero" values
    if (priceValue < 1e-15) {
      return displayBound === Bound.LOWER ? '0' : formatPrice(price, 3)
    }

    if (priceValue > 1e12) {
      return displayBound === Bound.UPPER ? '∞' : formatPrice(price, 3)
    }

    const is1BpsTier = feeAmount === FeeAmount.LOWEST

    if (is1BpsTier) {
      return price.toSignificant(5)
    } else {
      return formatPrice(price, optimalSigFigs)
    }
  } catch (error) {
    return formatPrice(price, optimalSigFigs)
  }
}
